import emojiRegex from 'emoji-regex'
import { parseBlocksV1Message } from '@screens/Chat/utils/chatbotBlocks'

export type SpeechChunk = { id: string; text: string }

const EMOJI_REGEX = emojiRegex()

// Nora responses are authored for rich chat rendering, not direct text-to-speech.
// Before handing them to Realtime audio we remove formatting that would sound
// awkward, while preserving user-facing words like link labels and inline code.
export const stripMarkdownForSpeech = (text: string): string => {
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*(?:[-*+]\s+|>\s?)/gm, ' ')
    .replace(/(^|\s)#+\s+/g, '$1')
    .replace(/[*_~]+/g, ' ')
    .replace(EMOJI_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;!?])/g, '$1')
    .trim()
}

// The Realtime request should stay comfortably below prompt limits and avoid
// asking the model to synthesize huge turns at once. Chunk on whitespace when we
// can, but fall back to a hard split for unusually long tokens.
export const chunkForSpeech = (text: string, maxLength = 900): string[] => {
  const clean = stripMarkdownForSpeech(text)
  if (!clean) {
    return []
  }
  if (clean.length <= maxLength) {
    return [clean]
  }

  const chunks: string[] = []
  let remaining = clean

  while (remaining.length > maxLength) {
    const boundary = remaining.lastIndexOf(' ', maxLength)
    const splitAt = boundary > maxLength * 0.5 ? boundary : maxLength
    chunks.push(remaining.slice(0, splitAt).trim())
    remaining = remaining.slice(splitAt).trim()
  }

  if (remaining) {
    chunks.push(remaining)
  }

  return chunks
}

// Nora messages are expected to come from the "nora" user, but some Stream
// payloads in tests/local flows are looser. Treat blocks_v1 as assistant output
// because user-authored messages should not contain Nora rich_text blocks.
const isAssistantMessage = (message: any) => {
  const userId = message?.user?.id || message?.user_id || message?.role
  return (
    userId === 'nora' ||
    userId === 'assistant' ||
    !!message?.blocks_v1 ||
    message?.render_mode === 'blocks_v1'
  )
}

const isThinkingPlaceholder = (text: string) => {
  const normalized = text.trim().replace(/…/g, '...')
  return normalized.toLowerCase() === 'thinking...'
}

const STABLE_PARTIAL_BOUNDARY = /\n\n|[.!?]["')\]]?\s/g

const stableSourceText = (text: string, status: string | undefined) => {
  if (status === 'complete') {
    return text
  }

  let boundaryEnd = 0
  for (const match of text.matchAll(STABLE_PARTIAL_BOUNDARY)) {
    boundaryEnd = match.index + match[0].length
  }

  return boundaryEnd > 0 ? text.slice(0, boundaryEnd) : ''
}

const blockSpeechSourceText = (block: any): string | undefined => {
  const data = block?.data
  if (typeof data?.text === 'string') {
    return data.text
  }
  if (typeof data?.title === 'string') {
    return data.title
  }
  return undefined
}

export const extractSpeechChunks = (
  message: any,
  spokenTextByBlockId: Record<string, string>,
): SpeechChunk[] => {
  if (!isAssistantMessage(message)) {
    return []
  }

  const parsedMessage = parseBlocksV1Message(message)

  const blocks =
    parsedMessage?.blocks ??
    (message.blocks_v1 ?? []).map((block: any, blockIndex: number) => ({
      id: String(blockIndex),
      kind: block?.type,
      status: block?.status ?? 'complete',
      data: { text: block?.text },
    }))

  return blocks.flatMap((block: any, blockIndex: number) => {
    const text = blockSpeechSourceText(block)
    if (typeof text !== 'string' || !text.trim() || isThinkingPlaceholder(text)) {
      return []
    }

    const blockId = `${message.id ?? 'message'}:${block.id ?? blockIndex}`
    const speechText = stripMarkdownForSpeech(stableSourceText(text, block?.status))
    const previousSpeechText = spokenTextByBlockId[blockId] ?? ''
    if (!speechText || speechText === previousSpeechText) {
      return []
    }

    if (previousSpeechText && !speechText.startsWith(previousSpeechText)) {
      return []
    }

    Object.assign(spokenTextByBlockId, { [blockId]: speechText })
    const appendedText = speechText.slice(previousSpeechText.length).trim()
    const chunkIdPrefix = previousSpeechText ? `${blockId}:${previousSpeechText.length}` : blockId

    return chunkForSpeech(appendedText).map((chunkText, chunkIndex) => ({
      id: `${chunkIdPrefix}:${chunkIndex}`,
      text: chunkText,
    }))
  })
}
